/**
 * ModuleGate — read-only mode for a page whose module the role can read but not write (F27,
 * ux-gmp-auditor #1).
 *
 * A read-only grant must still permit *reading*: searching, filtering, sorting, opening a detail
 * drawer, verifying a hash, downloading a file the role is allowed to see. It must block only the
 * acts that change state or sign something.
 *
 * The first version wrapped the whole page in `<fieldset disabled>`, which disables every native
 * control in the subtree — on `/record-exports` that killed Verify, Zip, the per-file downloads, the
 * Details drawer trigger, the table's own search box, column config and CSV export, so an inspector
 * could see a certified export listed and touch none of it. This version never disables a subtree.
 * Instead it intercepts activation in the capture phase and blocks only *write* controls:
 *
 *   • blocked   — anything marked `data-write-action`, plus the shapes an antd write control takes:
 *                 `type="primary"`, `danger`, `htmlType="submit"`, `<Switch>`, `<Upload>`.
 *   • allowed   — everything else, and anything inside a `data-readonly-allow` subtree (spread
 *                 `readOnlyAllow` onto the element): text, search boxes, filters, sorting, links,
 *                 default/text buttons such as Verify / Download / Details / Refresh / Export CSV.
 *
 * A blocked control is greyed, marked `aria-disabled`, and gets a `title` on hover/focus saying why —
 * it is never silently inert. Antd Drawers, Modals and Popconfirms render in a portal *outside* this
 * subtree, so while a page is read-only the same rule is also applied at document level inside those
 * portal roots (an "OK" / "Save" in a modal is `.ant-btn-primary`). A portal whose whole content is a
 * read is opted out with `rootClassName="readonly-allow-root"` on the Drawer / Modal. Controls whose
 * enabled state must be visible (rather than merely refused on click) should still gate themselves
 * with `useCanWrite`.
 *
 *   export default withModuleGate(PlantConfiguration, 'Plant Configuration');
 *   export default withModuleGate(SopDocuments, ['Building Blocks']);
 *   export default withModuleGate(RecordExports, [...], { message: 'You can read and download…' });
 *
 * UI gating only — the server enforces the same flags (djangoapp/utilities/permissions.py and, for
 * signed acts, djangoapp/utilities/module_write_guard.py); a 403 from the API is the real refusal.
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Alert, message as antdMessage } from 'antd';
import useModuleAccess, { getModuleAccess } from '@/hooks/useModuleAccess';

/** Spread onto any element whose subtree must stay fully usable in read-only mode. */
export const readOnlyAllow = { 'data-readonly-allow': 'true' };
/** `rootClassName` for an antd Drawer / Modal whose portalled content is all reads. */
export const READ_ONLY_PORTAL_CLASS = 'readonly-allow-root';
// Portal roots antd renders outside the gate's subtree.
const PORTAL_ROOTS = '.ant-modal-wrap,.ant-modal-root,.ant-drawer,.ant-popover,.ant-dropdown';
/** Spread onto a control that changes state or signs, so read-only mode blocks it explicitly. */
export const writeAction = { 'data-write-action': 'true' };

const DEFAULT_REASON = 'Read-only access — you can view and download this record but not change or sign one.';

// Shapes an antd write control takes. Kept deliberately narrow so reads stay alive: default and
// text buttons, links, inputs, selects and table controls are not matched.
const WRITE_SELECTOR = [
  '[data-write-action]',
  'button[type="submit"]',
  '.ant-btn-primary',
  '.ant-btn-dangerous',
  '.ant-switch',
  '.ant-upload',
].join(',');

const STYLE_ID = 'module-gate-readonly-style';
const CSS = `
[data-module-gate="read-only"] :is([data-write-action],button[type="submit"],.ant-btn-primary,.ant-btn-dangerous,.ant-switch,.ant-upload):not([data-readonly-allow] *):not([data-readonly-allow]) {
  opacity: 0.45;
  cursor: not-allowed;
  filter: grayscale(1);
}`;

function ensureStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

const isAllowed = (el) =>
  !!(el && el.closest && el.closest(`[data-readonly-allow],.${READ_ONLY_PORTAL_CLASS}`));
const writeControl = (el) => (el && el.closest ? el.closest(WRITE_SELECTOR) : null);

/** The element the event is really aimed at, or null when it is not a blocked write control. */
function blockedTarget(e) {
  const el = e.target;
  if (!el || !el.closest) return null;
  if (isAllowed(el)) return null;
  const ctl = writeControl(el);
  if (!ctl || isAllowed(ctl)) return null;
  return ctl;
}

/**
 * `useCanWrite(['Quality Management', 'Audit Logs'])` → { canWrite, loading }.
 *
 * The same "any of these modules grants create/update/delete" question ModuleGate asks, for pages that
 * gate their own controls — in particular controls inside an antd Drawer or Modal, which render in a
 * portal outside the gate's subtree and are therefore not covered by it.
 */
export function useCanWrite(modules) {
  const list = useMemo(() => (Array.isArray(modules) ? modules : [modules]).filter(Boolean), [modules]);
  // Subscribe through the hook (first module) so a refreshed access list re-renders the caller.
  const first = useModuleAccess(list[0]);
  const canWrite = list.some((m) => {
    const a = getModuleAccess(m);
    return a.create || a.update || a.del || a.isAdmin;
  });
  return { canWrite: first.loading ? true : canWrite, loading: first.loading };
}

export function ModuleGate({ modules, children, message }) {
  const list = useMemo(() => (Array.isArray(modules) ? modules : [modules]).filter(Boolean), [modules]);
  // Subscribe through the hook (first module) so a refreshed access list re-renders the gate;
  // any module in the list granting create/update/delete unlocks the page (menu uses anyOf()).
  const first = useModuleAccess(list[0]);
  const warned = useRef(0);
  const reason = message || DEFAULT_REASON;

  const refuse = useCallback(
    (e) => {
      const ctl = blockedTarget(e);
      if (!ctl) return;
      e.preventDefault();
      e.stopPropagation();
      const now = Date.now();
      if (now - warned.current > 1200) {
        warned.current = now;
        antdMessage.info({ content: reason, key: 'module-gate-readonly' });
      }
    },
    [reason]
  );

  const explain = useCallback(
    (e) => {
      const ctl = blockedTarget(e);
      if (!ctl || ctl.getAttribute('aria-disabled') === 'true') return;
      ctl.setAttribute('aria-disabled', 'true');
      if (!ctl.getAttribute('title')) ctl.setAttribute('title', reason);
    },
    [reason]
  );

  const onKey = useCallback(
    (e) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      refuse(e);
    },
    [refuse]
  );

  // Portalled Drawer / Modal / Popconfirm content is not inside the wrapper below, so the same rule is
  // applied at document level while the page is read-only. Scoped to portal roots and to write shapes,
  // so nothing outside a gated read-only page is affected.
  const readOnlyActive =
    !first.loading &&
    !list.some((m) => {
      const a = getModuleAccess(m);
      return a.create || a.update || a.del || a.isAdmin;
    });
  useEffect(() => {
    if (!readOnlyActive) return undefined;
    const onDoc = (e) => {
      if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      const el = e.target;
      if (!el || !el.closest || !el.closest(PORTAL_ROOTS)) return;
      const ctl = blockedTarget(e);
      if (!ctl) return;
      e.preventDefault();
      e.stopPropagation();
      antdMessage.info({ content: reason, key: 'module-gate-readonly' });
    };
    document.addEventListener('click', onDoc, true);
    document.addEventListener('keydown', onDoc, true);
    return () => {
      document.removeEventListener('click', onDoc, true);
      document.removeEventListener('keydown', onDoc, true);
    };
  }, [readOnlyActive, reason]);

  if (first.loading) return children;
  const canWrite = list.some((m) => {
    const a = getModuleAccess(m);
    return a.create || a.update || a.del || a.isAdmin;
  });
  if (canWrite) return children;
  ensureStyle();

  return (
    <div
      data-module-gate="read-only"
      aria-readonly="true"
      onClickCapture={refuse}
      onMouseDownCapture={refuse}
      onKeyDownCapture={onKey}
      onSubmitCapture={refuse}
      onPointerOverCapture={explain}
      onFocusCapture={explain}
    >
      <Alert
        type="info"
        showIcon
        banner
        message="Read-only"
        description={reason}
        style={{ margin: '8px 10px 0' }}
      />
      {children}
    </div>
  );
}

export function withModuleGate(Component, modules, options = {}) {
  const Gated = (props) => (
    <ModuleGate modules={modules} message={options.message}>
      <Component {...props} />
    </ModuleGate>
  );
  Gated.displayName = `withModuleGate(${Component.displayName || Component.name || 'Component'})`;
  return Gated;
}

export default ModuleGate;
